import { Box, Flex, Icon, useBreakpointValue } from '@chakra-ui/react';
import React from 'react';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { CustomText } from '../atoms/CustomText';
import { getIconFromString } from './MenuCard';
import { SectionIcon } from '../atoms/SectionIcon';

interface ArticleCardAnalyticsProps {
  id: string | number;
  category: string;
  title: string;
  categoryTextColor: string;
  faqArticlesBgColor: string;
  faqArticlesTextColor: string;
  onClickPopularArticle: (id: string | number) => void;
  primaryAccentColor?: string;
  secondaryAccentColor?: string;
  icon: string;
  iconType?: 'icon' | 'svg';
  popularArticleHref?: (id: string | number) => string;
}

export const ArticleCardAnalytics: React.FC<ArticleCardAnalyticsProps> = ({
  id,
  category,
  title,
  categoryTextColor,
  faqArticlesBgColor,
  faqArticlesTextColor,
  onClickPopularArticle,
  primaryAccentColor,
  secondaryAccentColor,
  icon,
  iconType,
  popularArticleHref,
}) => {
  const headingAlign = useBreakpointValue({ base: 'left', md: 'center' }) as 'left' | 'center';
  return (
    <a
      href={popularArticleHref ? popularArticleHref(id) : undefined}
      style={{ textDecoration: 'none' }}
    >
      <Box
        position="relative"
        px="5"
        border={`1px solid ${secondaryAccentColor}`}
        boxShadow="sm"
        _hover={{ boxShadow: 'md', transform: 'scale(1.03)' }}
        cursor="pointer"
        maxW="100%"
        bg={faqArticlesBgColor}
        minHeight={{ base: '110px', sm: '120px', md: '15vh' }} // responsive min height
        onClick={e => {
          const isLeftClick = e.button === 0;
          const isModifiedClick = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
          // SPA routing on normal click
          if (isLeftClick && !isModifiedClick) {
            e.preventDefault();
            onClickPopularArticle(id);
          }
        }}
        overflow="visible" // allow icon not to get clipped
        boxSizing="border-box"
        py={{ base: 2, md: 0 }}
      >
        {/* Responsive layout: row on mobile (icon left, title right), column on desktop (original) */}
        <Flex
          direction={{ base: 'row', md: 'column' }}
          justify="center"
          align="center"
          height="100%"
          textAlign={{ base: 'left', md: 'center' }}
          gap={{ base: 2, md: 3 }}
        >
          {/* LEFT SIDE (Icon + Category on Mobile) */}
          <Box
            width={{ base: '30%', md: '100%' }}
            display="flex"
            flexDirection="column"
            alignItems={{ base: 'flex-start', md: 'center' }} // <-- responsive: left on mobile, center on md+
            justifyContent="center"
            px={{ base: 2, md: 0 }}
          >
            <Box
              // ensure the icon never exceeds container, and scale responsively
              width={{ base: '48px', sm: '48px', md: '56px' }}
              height={{ base: '48px', sm: '48px', md: '56px' }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              mt={{ base: 0, md: 2 }} // small top margin on md+ to avoid clipping
              overflow="visible" // allow slight overflow if needed
              mb={{ base: 0, md: 2 }}
            >
              {iconType === 'svg' ? (
                // SectionIcon should accept width/height; pass explicit props to ensure it scales
                <SectionIcon
                  color={primaryAccentColor}
                  svgPath={icon}
                  width="100%"
                  height="100%"
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                />
              ) : (
                <Icon
                  as={getIconFromString(icon) || FaRegQuestionCircle}
                  boxSize={{ base: 8, sm: 9, md: 10 }} // responsive box size
                  color={primaryAccentColor}
                  mb="0"
                />
              )}
            </Box>

            {/* Category under icon on mobile (left side); center on md */}
            <CustomText
              variant="body"
              fontSize={{ base: 'xs', md: 'xs', lg: 'sm' }}
              fontWeight="normal"
              color={categoryTextColor}
              mt="1"
              textAlign={headingAlign}
              width="100%"
            >
              {category}
            </CustomText>
          </Box>

          {/* RIGHT SIDE (Title on Mobile) */}
          <Box
            width={{ base: '70%', md: '100%' }}
            display="flex"
            alignItems="center"
            justifyContent={{ base: 'flex-start', md: 'center' }}
            overflow="hidden"
            px={{ base: 2, md: 0 }}
          >
            <CustomText
              variant="h3"
              fontSize={{ base: 'md', md: 'md', lg: 'lg' }}
              fontWeight="bold"
              color={faqArticlesTextColor}
              textAlign={headingAlign}
              noOfLines={2}
              // ensure the title can shrink inside the flex container
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
              }}
            >
              {title}
            </CustomText>
          </Box>
        </Flex>

        {/* Triangle arrow (unchanged) */}
        <Box
          position="absolute"
          bottom="0"
          right="0"
          width="0"
          height="0"
          borderLeft="30px solid transparent"
          borderTop={`30px solid ${secondaryAccentColor}`}
          transform="rotate(90deg)"
          pointerEvents="none"
        >
          <Box
            position="absolute"
            bottom="8px"
            right="2px"
            color="white"
            fontSize="18px"
            fontWeight="bold"
            transform="rotate(270deg)"
          >
            →
          </Box>
        </Box>
      </Box>
    </a>
  );
};
